import React, { useState, useEffect, useRef } from 'react';
import type { CryptoCurrency } from '../../utils/coingecko';
import { useSearchCryptocurrencies } from '../../utils/hooks/useSearchCryptocurrencies';

interface CryptoSelectProps {
  value?: CryptoCurrency | null;
  onChange: (crypto: CryptoCurrency | null) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

export const CryptoSelect: React.FC<CryptoSelectProps> = ({
  value,
  onChange,
  placeholder = "Rechercher une cryptomonnaie...",
  required = false,
  className = '',
}) => {
  const [inputValue, setInputValue] = useState(value?.name || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Nettoyer le timer au démontage pour éviter les mises à jour sur composant démonté
  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  // React Query hook for crypto search
  const { data: suggestions = [], isLoading } = useSearchCryptocurrencies(debouncedSearchTerm);

  // Synchroniser inputValue avec la prop value seulement si l'utilisateur ne tape pas
  useEffect(() => {
    if (!isUserTyping) {
      setInputValue(value?.name || '');
    }
  }, [value, isUserTyping]);

  // Debounce the search term
  useEffect(() => {
    if (isUserTyping && inputValue && inputValue.length >= 2) {
      const timeoutId = setTimeout(() => {
        setDebouncedSearchTerm(inputValue);
        setShowSuggestions(true);
      }, 800); // 800ms debounce delay

      return () => clearTimeout(timeoutId);
    } else if (inputValue.length < 2) {
      setDebouncedSearchTerm('');
      setShowSuggestions(false);
    }
  }, [inputValue, isUserTyping]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);
    setIsUserTyping(true);
    
    if (!newValue) {
      onChange(null);
      setIsUserTyping(false);
    }
  };

  const handleSuggestionClick = (crypto: CryptoCurrency) => {
    setIsUserTyping(false); // Arrêter le mode "utilisateur tape"
    setInputValue(crypto.name);
    setShowSuggestions(false);
    setDebouncedSearchTerm(''); // Clear the search results
    setSelectedIndex(-1);
    // Appeler onChange immédiatement
    onChange(crypto);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSuggestionClick(suggestions[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
      setIsUserTyping(false);
    }
  };

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
      
      // Seulement créer une crypto personnalisée si l'utilisateur a tapé quelque chose
      // et qu'il n'y a pas de valeur sélectionnée ET qu'il n'a pas cliqué sur une suggestion
      if (isUserTyping && inputValue && !value && inputValue.length >= 2) {
        const customCrypto: CryptoCurrency = {
          id: `custom-${inputValue.toLowerCase().replace(/\s+/g, '-')}`,
          symbol: inputValue.toUpperCase(),
          name: inputValue,
        };
        onChange(customCrypto);
      } else if (!value && inputValue) {
        // Si pas de valeur et que l'input n'est pas vide, vider l'input
        setInputValue('');
      }
      
      setIsUserTyping(false);
    }, 200);
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => {
          if (suggestions.length > 0 && isUserTyping) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        required={required}
        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
      
      {showSuggestions && (suggestions.length > 0 || isLoading) && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="px-3 py-2 text-gray-500 dark:text-gray-400">Recherche en cours...</div>
          ) : (
            suggestions.map((crypto, index) => (
              <div
                key={crypto.id}
                onMouseDown={(e) => {
                  e.preventDefault(); // Empêcher onBlur de se déclencher
                  handleSuggestionClick(crypto);
                }}
                className={`px-3 py-2 cursor-pointer flex justify-between items-center ${
                  index === selectedIndex
                    ? 'bg-blue-100 text-blue-900'
                    : 'hover:bg-gray-100 hover:dark:bg-gray-700'
                }`}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{crypto.name}</span>
                  {crypto.source === 'livecoinwatch' && (
                    <span className="text-xs text-orange-500">via LiveCoinWatch</span>
                  )}
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400 uppercase">
                  {crypto.symbol}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

